<script setup lang="ts">
import { onMounted, ref } from 'vue';

// types
import { IPermission } from '../../../types/user.type';

//composables
import { useMessage } from '../../../composables/useMessage';
import { useToast } from '../../../composables/useToast';

//repositories
import UserApi from '@repositories/user.repository';
import Store from '@store/dataProvider';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const userApiRepository = new UserApi();

const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const emit = defineEmits<{
  (e: 'closeModal'): void;
}>();

const permissionData = ref<IPermission[]>([]);
const title = ref<string>('');
const description = ref<string>('');
const loading = ref<boolean>(false);
const store = Store();

onMounted(async () => {
  try {
    loading.value = true;
    const { data } = await userApiRepository.getPermissions();
    if (Array.isArray(data)) {
      permissionData.value = data.sort((a, b) =>
        a.descripcion.localeCompare(b.descripcion),
      );
    }
  } catch (error) {
    showMessage(t('global.ocurrio_un_error'), 'error');
  } finally {
    loading.value = false;
  }
});

const handleCreateRol = async () => {
  if (!title.value || !description.value) {
    showMessage(t('global.debes_completar_los_campos_requeridos'), 'error');
    return;
  }

  const selectedPermissions = permissionData.value.map((permission: any) => ({
    ...permission,
    selected: permission.selected ?? false,
  }));

  if (!selectedPermissions.some((permission: any) => permission.selected)) {
    showMessage(
      t('ajustes.usuarios.modalContentRolesCreate.debes_seleccionar_al'),
      'error',
    );
    return;
  }

  const newRole = {
    title: title.value,
    description: description.value,
    permissions: selectedPermissions,
  };
  try {
    const { data } = await userApiRepository.createRole(newRole);
    store.pushItem({
      key: 'roles',
      value: data,
    });
    $toast?.success(t('ajustes.usuarios.modalContentRolesCreate.rol_creado_con_exito'));
    emit('closeModal');
  } catch (error: any) {
    showMessage(error.message, 'error');
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="d-block position-relative min-h-100"
    style="min-height: 100px"
  >
    <Overlay
      :show="true"
      :circleColor="'#40a4c6'"
      :borderColor="'#40a4c6'"
      class="h-100 w-100"
    />
  </div>
  <div v-else>
    <form @submit.prevent="handleCreateRol">
      <div class="d-flex">
        <div class="w-100 mx-1">
          <label class="m-1 w-100">{{t('global.nombre')}}</label>
          <Input :placeholder="t('global.nombre')" class="mb-2" v-model="title" />
        </div>
        <div class="w-100 mx-1">
          <label class="m-1">{{t('global.descripcion')}}</label>
          <Input :placeholder="t('global.descripcion')" class="mb-2" v-model="description" />
        </div>
      </div>
      <label class="m-1">{{t('ajustes.usuarios.modalContentRolesCreate.permisos')}}</label>

      <div class="row">
        <div
          v-for="permission in permissionData"
          :key="permission.id"
          class="col-12 col-sm-6 col-md-4 col-lg-3 mb-2"
        >
          <div class="d-flex align-items-center p-2 rounded">
            <CheckBox
              :option="false"
              @change="permission.selected = !permission.selected"
              :value="!permission.selected"
            />
            <p class="m-1 mb-0 flex-grow-1 text-truncate">
              {{ permission.descripcion }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="messageHTML" v-html="messageHTML" />
      <CButton
        class="w-100 fs-6 p-2 mt-2"
        background="background-blue"
        style="padding: 0.5rem !important"
        type="submit"
      >
        <template #content> {{t('global.crear')}} </template>
      </CButton>
    </form>
  </div>
</template>
